<template>
  <div class="container scheduling-page">
    <ul class="nav nav-tabs nav-fill nav-tabs-sm">
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{'active': activeTab === 'dashboard'}"
          @click="changeTabs('dashboard')"
        >
          {{ $t("SmartScheduling.Tabs.Dashboard") }}
        </a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{'active': activeTab === 'personal'}"
          @click="changeTabs('personal')"
        >
          {{ $t("SmartScheduling.Tabs.PersonalSetting") }}
        </a>
      </li>
      <li class="nav-item">
        <a
          class="nav-link"
          :class="{
            'active': activeTab === 'common',
            'disabled': isUser
          }"
          @click="changeTabs('common')"
        >
          {{ $t("SmartScheduling.Tabs.CommonSetting") }}
        </a>
      </li>
    </ul>
    <div class="tab-contents">
      <div v-show="activeTab === 'dashboard'">
        <dashboard :dashboard-data="dashboardDataArray" />
      </div>
      <div v-show="activeTab === 'personal'">
        <div class="container">
          <div class="card md-12">
            <div class="card-body">
              <h5 class="card-title">
                {{ $t("SmartScheduling.Personal.Label.CalendarLinkSetting") }}
              </h5>
              <div class="card-text">
                <link-calendar-setting
                  @changeLoginGoogle="changeLoginGoogle"
                  @changeLoginMicrosoft="changeLoginMicrosoft"
                />
              </div>
            </div>
          </div>
        </div>
        <personal-setting-form
          :personal-setting-data="personalSettingData"
          @updateEditData="updataPersonalData"
        />
        <div class="container">
          <button
            type="button"
            class="btn btn-secondary btn-block"
            :disabled="!isChangedPersonal"
            @click="saveUserSettingHandler"
          >
            {{ $t("General.Save") }}
          </button>
        </div>
      </div>
      <div v-show="activeTab === 'common'">
        <common-setting-form
          :common-setting-data="commonSettingData"
          @updateEditData="updateCommonData"
        />
        <div class="container">
          <div class="card md-12">
            <div class="card-body">
              <h5 class="card-title">
                {{ $t("SmartScheduling.Personal.Label.RoomSetting") }}
              </h5>
              <div class="card-text">
                <meeting-room-setting-form
                  :items="rooms"
                  @save="updateRooms"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="container">
          <button
            type="button"
            class="btn btn-secondary btn-block"
            :disabled="!isChangedCommon && !isChangeRoom"
            @click="saveCompanySettingHandler"
          >
            {{ $t("General.Save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Dashboard from './components/Dashboard.vue'
import PersonalSettingForm from './components/PersonalSettingForm.vue'
import LinkCalendarSetting from './components/LinkCalendarSetting.vue'
import CommonSettingForm from './components/CommonSettingForm.vue'
import MeetingRoomSettingForm from './components/MeetingRoomSettingForm.vue'
import firebaseHelper from '@/helper/firebaseHelper'

import { mapState, mapActions, mapGetters } from 'vuex'

export default {
  title: 'SmartScheduling',
  components: {
    Dashboard,
    PersonalSettingForm,
    LinkCalendarSetting,
    CommonSettingForm,
    MeetingRoomSettingForm
  },
  data: () => ({
    ...mapState([
      'lang'
    ]),
    ...mapGetters('scheduling', [
      'getCompanySettingData',
      'getUserSettingData'
    ]),
    activeTab: 'dashboard',
    dashboardDataArray: [],
    personalEditData: {},
    isChangedPersonal: false,
    commonEditData: {},
    isChangedCommon: false,
    roomEditData: [],
    isChangeRoom: false,
  }),
  computed: {
    ...mapState('auth', [
      'uid',
      'role'
    ]),
    ...mapState('scheduling', [
      'dashboardData',
      'rooms'
    ]),
    commonSettingData() {
      return this.getCompanySettingData()
    },
    personalSettingData() {
      return this.getUserSettingData()
    },
    isUser() {
      return this.role === 'Member'
    }
  },
  mounted() {
    // NOTE: Read Some Settind Datas
    this.setLoadingState('loadSchedulingData')
    this.setApiUserAuth().then(() => {
      return Promise.all([
        this.loadDashboard(this.uid),
        this.loadUserSetting(this.uid),
        this.loadRooms(this.uid),
        this.loadCompanySetting(this.uid)
      ])
    }).finally(() => {
      this.isChangedCommon = false
      this.isChangedPersonal = false
      this.isChangeRoom = false
      this.unsetLoadingState('loadSchedulingData')
    })
  },
  watch: {
    dashboardData: {
      handler: function (val, _) {
        console.log('changed')
        this.dashboardDataArray = [].concat(val)
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'setLanguage',
      'setLoadingState',
      'unsetLoadingState',
      'setToastStack'
    ]),
    ...mapActions('auth', [
      'setApiUserAuth',
      'setLoginByGoogle',
      'setLoginByMicrosoft'
    ]),
    ...mapActions('scheduling', [
      'loadDashboard',
      'loadUserSetting',
      'saveUserSetting',
      'loadCompanySetting',
      'saveCompanySetting',
      'loadRooms',
      'saveRooms'
    ]),
    changeTabs (target) {
      this.activeTab = target
    },
    updataPersonalData(d) {
      this.personalEditData = Object.assign({}, d)
      if (d.userLang) {
        this.setLanguage(d.userLang)
      }
      this.isChangedPersonal = true
    },
    saveUserSettingHandler() {
      this.setLoadingState('saveUserSetting')
      this.saveUserSetting({
        uid: this.uid,
        data: this.personalEditData
      }).then(() => {
        this.isChangedPersonal = false
        this.setToastStack({
          type: 'success',
          title: this.$t('SmartScheduling.Toasts.SavePersonal.Title'),
          message: this.$t('SmartScheduling.Toasts.SavePersonal.Success'),
          show: 5
        })
      }).catch(() => {
        this.setToastStack({
          type: 'error',
          title: this.$t('SmartScheduling.Toasts.SavePersonal.Title'),
          message: this.$t('SmartScheduling.Toasts.SavePersonal.Error'),
          show: 5
        })
      }).finally(() => {
        this.unsetLoadingState('saveUserSetting')
      })
    },
    changeLoginGoogle() {
      this.setLoadingState('changeLoginMethod')
      firebaseHelper.signOut().then(() => {
        return firebaseHelper.signInGoogle()
      }).then((res) => {
        return this.setLoginByGoogle(res)
      }).then(() => {
        this.$router.push({ name: 'SmartScheduling' })
      }).catch((err) => {
        this.$router.push({
          name: 'login',
          lang: this.lang
        })
      }).finally(() => {
        this.unsetLoadingState('changeLoginMethod')
      })
    },
    changeLoginMicrosoft() {
      this.setLoadingState('changeLoginMethod')
      firebaseHelper.signOut().then(() => {
        return firebaseHelper.signInMicrosoft()
      }).then((res) => {
        return this.setLoginByMicrosoft(res)
      }).then(() => {
        this.$router.push({ name: 'SmartScheduling' })
      }).catch((err) => {
        this.$router.push({
          name: 'login',
          lang: this.lang
        })
      }).finally(() => {
        this.unsetLoadingState('changeLoginMethod')
      })
    },
    updateCommonData(d) {
      this.commonEditData = Object.assign({}, d)
      this.isChangedCommon = true
    },
    updateRooms (d) {
      this.roomEditData = [].concat(d)
      this.isChangeRoom = true
    },
    saveCompanySettingHandler() {
      if (isChangedCommon) {
        this.setLoadingState('saveCompanySetting')
        this.saveCompanySetting({
          uid: this.uid,
          data: this.commonEditData
        }).then(() => {
          this.isChangedCommon = false
          this.setToastStack({
            type: 'success',
            title: this.$t('SmartScheduling.Toasts.SaveCommon.Title'),
            message: this.$t('SmartScheduling.Toasts.SaveCommon.Success'),
            show: 5
          })
        }).catch(() => {
          this.setToastStack({
            type: 'error',
            title: this.$t('SmartScheduling.Toasts.SaveCommon.Title'),
            message: this.$t('SmartScheduling.Toasts.SaveCommon.Error'),
            show: 5
          })
        }).finally(() => {
          this.unsetLoadingState('saveCompanySetting')
        })
      }
      if (isChangeRoom) {
        this.setLoadingState('saveRoomsData')
        this.saveRooms({
          uid: this.uid,
          data: this.roomEditData
        }).then(() => {
          this.isChangeRoom = false
          this.setToastStack({
            type: 'success',
            title: this.$t('SmartScheduling.Toasts.SaveRooms.Title'),
            message: this.$t('SmartScheduling.Toasts.SaveRooms.Success'),
            show: 5
          })
        }).catch(() => {
          this.setToastStack({
            type: 'error',
            title: this.$t('SmartScheduling.Toasts.SaveRooms.Title'),
            message: this.$t('SmartScheduling.Toasts.SaveRooms.Error'),
            show: 5
          })
        }).finally(() => {
          this.unsetLoadingState('saveRoomsData')
        })
      }
    },
  }
}

</script>

<style lang="scss" scoped>
.tab-contents {
  padding: 24px 12px;
}
</style>
